import { useEffect, useState } from "react";
import { Plus, Upload, Camera, Trash2, X, Sparkles, Search, Receipt as ReceiptIcon, TrendingUp, Calendar, FileDown, Printer, Download } from "lucide-react";
import api, { formatErr, fileUrl, getToken } from "../lib/apiClient";
import { fmtCAD, fmtDate, todayIso } from "../lib/format";

const CATEGORIES = ["Materials", "Fuel", "Tools", "Office Supplies", "Vehicle", "Insurance", "Subscriptions", "Other"];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [hstReport, setHstReport] = useState(null);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState(new Set());
  const [pdfBusy, setPdfBusy] = useState(false);

  /* --- Item 6: single- and batch-expense PDF helpers ---------------------- */
  const downloadBlob = (blob, filename) => {
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(href);
  };

  const openPdfInNewTab = (blob, printAfterOpen) => {
    const href = URL.createObjectURL(blob);
    const w = window.open(href, "_blank", "noopener,noreferrer");
    if (printAfterOpen && w) {
      // Wait for the PDF viewer to render before invoking print.
      w.addEventListener("load", () => setTimeout(() => { try { w.print(); } catch (e) { /* user must trigger */ } }, 700));
    }
    setTimeout(() => URL.revokeObjectURL(href), 60_000);
  };

  const singleExpensePdf = async (expenseId, filename, mode = "download") => {
    setPdfBusy(true);
    try {
      const token = getToken();
      const res = await fetch(fileUrl(`/expenses/${expenseId}/pdf`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (mode === "print") openPdfInNewTab(blob, true);
      else downloadBlob(blob, filename);
    } catch (e) { setErr(`PDF failed: ${e.message}`); }
    finally { setPdfBusy(false); }
  };

  const batchPdf = async (mode = "download") => {
    if (selected.size === 0) return;
    setPdfBusy(true);
    try {
      const token = getToken();
      const res = await fetch(fileUrl(`/expenses/batch-pdf`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ expense_ids: [...selected] }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const stamp = new Date().toISOString().slice(0, 10);
      if (mode === "print") openPdfInNewTab(blob, true);
      else downloadBlob(blob, `expenses-batch-${stamp}.pdf`);
    } catch (e) { setErr(`Batch PDF failed: ${e.message}`); }
    finally { setPdfBusy(false); }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => prev.size === items.length ? new Set() : new Set(items.map((x) => x.id)));
  };
  const allSelected = items.length > 0 && selected.size === items.length;

  const load = async () => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (category !== "all") p.set("category", category);
    const [{ data }, { data: jj }, { data: rep }] = await Promise.all([
      api.get(`/expenses?${p}`),
      api.get("/jobs?include_closed=true"),
      api.get(`/expenses/hst-report?year=${reportYear}`).catch(() => ({ data: null })),
    ]);
    setItems(data); setJobs(jj); setHstReport(rep);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search, category, reportYear]);

  const blank = { vendor: "", date: todayIso(), amount: 0, hst_amount: 0, category: "Materials", notes: "", job_id: null, receipt_path: null };
  const openNew = () => { setEditing(blank); setShowForm(true); setPreviewUrl(null); };
  const openEdit = (it) => { setEditing(it); setShowForm(true); setPreviewUrl(null); };

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = {
        ...editing,
        amount: Number(editing.amount),
        hst_amount: Number(editing.hst_amount || 0),
        job_id: editing.job_id || null,
      };
      if (editing.id) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      setShowForm(false); setEditing(null);
      await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/expenses/${id}`); await load();
  };

  // OCR + Upload combined: extract fields and store receipt path
  const handleReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    setErr("");
    try {
      // local preview
      setPreviewUrl(URL.createObjectURL(file));
      // Upload receipt
      const fd1 = new FormData(); fd1.append("file", file);
      const { data: up } = await api.post("/expenses/upload-receipt", fd1, { headers: { "Content-Type": "multipart/form-data" } });
      // OCR
      const fd2 = new FormData(); fd2.append("file", file);
      const { data: parsed } = await api.post("/expenses/ocr", fd2, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing({
        ...(editing || blank),
        vendor: parsed.vendor || (editing?.vendor || ""),
        date: parsed.date || (editing?.date || todayIso()),
        amount: parsed.amount != null ? parsed.amount : (editing?.amount || 0),
        hst_amount: parsed.hst_amount != null ? parsed.hst_amount : 0,
        category: parsed.suggested_category || (editing?.category || "Materials"),
        notes: parsed.notes || (editing?.notes || ""),
        receipt_path: up.path,
      });
    } catch (e) {
      setErr(formatErr(e));
      // still attach receipt path attempt
    } finally {
      setOcrLoading(false);
      e.target.value = "";
    }
  };

  const totalMtd = items.filter((x) => x.date >= new Date().toISOString().slice(0, 7) + "-01").reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalAll = items.reduce((s, x) => s + Number(x.amount || 0), 0);

  return (
    <div data-testid="expenses-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Expenses</h1><p className="text-sm text-slate-500 mt-1">{items.length} expense{items.length !== 1 ? "s" : ""} · {fmtCAD(totalAll)} total · {fmtCAD(totalMtd)} this month</p></div>
        <button onClick={openNew} data-testid="add-expense-btn" className="self-start px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={16} /> Add Expense</button>
      </div>

      <HstReportCard report={hstReport} year={reportYear} setYear={setReportYear} />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input data-testid="expense-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendor or notes..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
        </div>
        <select data-testid="expense-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md">
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {selected.size > 0 && (
          <div data-testid="expense-batch-bar" className="px-4 py-3 bg-[#0F4C81]/5 border-b border-[#0F4C81]/20 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium text-[#0F4C81]">{selected.size} expense{selected.size !== 1 ? "s" : ""} selected</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => batchPdf("download")}
                disabled={pdfBusy}
                data-testid="batch-download-pdf-btn"
                className="px-3 py-1.5 text-xs bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded-md font-medium flex items-center gap-1.5 disabled:opacity-50"
              >
                <Download size={13} /> Download PDF
              </button>
              <button
                onClick={() => batchPdf("print")}
                disabled={pdfBusy}
                data-testid="batch-print-pdf-btn"
                className="px-3 py-1.5 text-xs border border-[#0F4C81] text-[#0F4C81] rounded-md font-medium flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#0F4C81]/10"
              >
                <Printer size={13} /> Print
              </button>
              <button
                onClick={() => setSelected(new Set())}
                data-testid="batch-clear-btn"
                className="text-xs text-slate-500 hover:text-slate-900 ml-2"
              >
                Clear
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    data-testid="expense-select-all"
                    aria-label="Select all expenses"
                    className="w-4 h-4 accent-[#0F4C81] cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Date</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Vendor</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Category</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Job</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">HST</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Amount</th>
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No expenses yet.</td></tr>}
              {items.map((x) => {
                const isSel = selected.has(x.id);
                const filename = `expense-${(x.vendor || "expense").replace(/\s+/g, "-").slice(0, 24)}-${x.date}.pdf`;
                return (
                <tr
                  key={x.id}
                  data-testid={`expense-row-${x.id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? "bg-[#0F4C81]/5" : ""}`}
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleOne(x.id)}
                      data-testid={`expense-select-${x.id}`}
                      aria-label={`Select expense ${x.vendor || x.id}`}
                      className="w-4 h-4 accent-[#0F4C81] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(x.date)}</td>
                  <td className="px-4 py-3"><div className="font-medium">{x.vendor}</div>{x.receipt_path && <a href={fileUrl(`/receipts/download?path=${encodeURIComponent(x.receipt_path)}`)} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0F4C81] hover:underline flex items-center gap-1 mt-0.5"><ReceiptIcon size={11} /> receipt</a>}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{x.category}</span></td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600">{jobs.find((j) => j.id === x.job_id)?.number || "—"}</td>
                  <td className="px-4 py-3 text-right tabular text-slate-500">{fmtCAD(x.hst_amount)}</td>
                  <td className="px-4 py-3 text-right tabular font-medium">{fmtCAD(x.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => singleExpensePdf(x.id, filename, "download")}
                        disabled={pdfBusy}
                        data-testid={`row-pdf-btn-${x.id}`}
                        title="Download one-page PDF"
                        className="p-1.5 text-slate-500 hover:text-[#0F4C81] hover:bg-[#0F4C81]/10 rounded"
                      >
                        <FileDown size={14} />
                      </button>
                      <button
                        onClick={() => singleExpensePdf(x.id, filename, "print")}
                        disabled={pdfBusy}
                        data-testid={`row-print-btn-${x.id}`}
                        title="Print"
                        className="p-1.5 text-slate-500 hover:text-[#0F4C81] hover:bg-[#0F4C81]/10 rounded"
                      >
                        <Printer size={14} />
                      </button>
                      <button onClick={() => openEdit(x)} className="text-xs text-[#0F4C81] hover:underline ml-1 mr-1">Edit</button>
                      <button onClick={() => del(x.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={save} data-testid="expense-form" className="bg-white rounded-md max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center"><h3 className="font-display text-xl font-bold">{editing.id ? "Edit Expense" : "New Expense"}</h3><button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button></div>
            {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}

            <div className="bg-gradient-to-r from-[#0F4C81]/5 to-[#E0F2FE] border border-[#0F4C81]/20 rounded-md p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={16} className="text-[#0F4C81]" />
                <h4 className="font-medium text-sm">AI Receipt Scanner</h4>
              </div>
              <p className="text-xs text-slate-600 mb-3">Upload a receipt photo to auto-fill the form. Powered by GPT-4o-mini vision.</p>
              <div className="flex items-center gap-3 flex-wrap">
                <label data-testid="ocr-upload-btn" className="px-3 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md flex items-center gap-2 cursor-pointer">
                  <Camera size={14} /> {ocrLoading ? "Scanning..." : "Scan Receipt"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleReceipt} disabled={ocrLoading} />
                </label>
                {previewUrl && <img src={previewUrl} alt="" className="h-16 w-16 object-cover rounded border border-slate-200" />}
                {editing.receipt_path && !previewUrl && (
                  <a href={fileUrl(`/receipts/download?path=${encodeURIComponent(editing.receipt_path)}`)} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0F4C81] hover:underline">view stored receipt</a>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Vendor *</label><input data-testid="expense-vendor" value={editing.vendor} onChange={(e) => setEditing({ ...editing, vendor: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Date *</label><input data-testid="expense-date" type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Amount *</label><input data-testid="expense-amount" type="number" step="0.01" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md tabular" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">HST Paid</label><input data-testid="expense-hst" type="number" step="0.01" value={editing.hst_amount} onChange={(e) => setEditing({ ...editing, hst_amount: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md tabular" /></div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Category</label>
                <select data-testid="expense-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div><label className="text-xs uppercase text-slate-700 tracking-wider">Link to Job</label>
                <select data-testid="expense-job" value={editing.job_id || ""} onChange={(e) => setEditing({ ...editing, job_id: e.target.value || null })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md">
                  <option value="">— None —</option>
                  {jobs.map((j) => <option key={j.id} value={j.id}>{j.number} {j.job_type ? "· " + j.job_type : ""}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><label className="text-xs uppercase text-slate-700 tracking-wider">Notes</label><textarea data-testid="expense-notes" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={5} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md" /></div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="submit" data-testid="save-expense-btn" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">Save Expense</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* ---------- HST Report Card ---------- */

function HstReportCard({ report, year, setYear }) {
  if (!report) return null;
  const monthMax = Math.max(1, ...report.by_month.map((m) => m.hst_paid));
  const currentMonth = new Date().getMonth() + 1;
  const years = [];
  const thisYear = new Date().getFullYear();
  for (let y = thisYear; y >= thisYear - 4; y--) years.push(y);

  return (
    <section data-testid="hst-report" className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-[#0F4C81]/5 to-transparent">
        <div className="flex items-center gap-2">
          <TrendingUp size={15} className="text-[#0F4C81]"/>
          <h3 className="font-display font-semibold text-slate-900">HST paid · Input Tax Credits</h3>
        </div>
        <select
          data-testid="hst-report-year"
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          className="px-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </header>
      <div className="px-5 py-4 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile label="HST paid · MTD" value={fmtCAD(report.mtd_hst_paid)} sub={`of ${fmtCAD(report.mtd_total_spent)} spent`} color="#0F4C81" testid="hst-mtd"/>
          <StatTile label="HST paid · YTD" value={fmtCAD(report.ytd_hst_paid)} sub={`of ${fmtCAD(report.ytd_total_spent)} spent`} color="#10B981" testid="hst-ytd"/>
          <StatTile label="Avg HST / month" value={fmtCAD(report.ytd_hst_paid / Math.max(currentMonth, 1))} sub="Year-to-date" color="#8B5CF6" testid="hst-avg"/>
          <StatTile label="Top category" value={report.by_category[0]?.category || "—"} sub={report.by_category[0] ? fmtCAD(report.by_category[0].hst_paid) : "no data"} color="#F59E0B" testid="hst-top-cat"/>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2 flex items-center gap-1.5">
            <Calendar size={11}/> Monthly HST paid · {year}
          </div>
          <div className="grid grid-cols-12 gap-1.5 items-end h-32" data-testid="hst-month-chart">
            {report.by_month.map((m) => {
              const pct = monthMax > 0 ? (m.hst_paid / monthMax) * 100 : 0;
              const isCurrent = m.month === currentMonth && year === thisYear;
              return (
                <div key={m.month} className="flex flex-col items-center group" title={`${m.label}: ${fmtCAD(m.hst_paid)} HST (${fmtCAD(m.total_spent)} spent)`}>
                  <div className="text-[9px] tabular text-slate-500 mb-1 opacity-0 group-hover:opacity-100 transition-opacity">{m.hst_paid > 0 ? fmtCAD(m.hst_paid) : ""}</div>
                  <div className="w-full bg-slate-100 rounded-sm relative flex-1 flex items-end overflow-hidden">
                    <div
                      data-testid={`hst-bar-${m.month}`}
                      className={`w-full rounded-sm transition-all ${isCurrent ? "bg-[#0F4C81]" : "bg-[#0F4C81]/60 group-hover:bg-[#0F4C81]"}`}
                      style={{ height: `${pct}%`, minHeight: m.hst_paid > 0 ? "3px" : "0" }}
                    />
                  </div>
                  <div className={`text-[10px] mt-1 ${isCurrent ? "text-[#0F4C81] font-bold" : "text-slate-500"}`}>{m.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {report.by_category.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">By category · YTD</div>
            <div className="overflow-x-auto" data-testid="hst-by-category">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left py-2 font-medium">Category</th>
                    <th className="text-right py-2 font-medium">Items</th>
                    <th className="text-right py-2 font-medium">Total spent</th>
                    <th className="text-right py-2 font-medium">HST paid (ITC)</th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_category.map((c) => (
                    <tr key={c.category} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-2 text-slate-700"><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{c.category}</span></td>
                      <td className="py-2 text-right tabular text-slate-500">{c.count}</td>
                      <td className="py-2 text-right tabular text-slate-700">{fmtCAD(c.total_spent)}</td>
                      <td className="py-2 text-right tabular font-semibold text-[#0F4C81]">{fmtCAD(c.hst_paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function StatTile({ label, value, sub, color, testid }) {
  return (
    <div data-testid={testid} className="border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full inline-block" style={{ background: color }} />
        {label}
      </div>
      <div className="font-display text-xl font-bold tabular mt-1" style={{ color }}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

